import React from 'react';
import './McpSessionGrantPill.css';

/**
 * The always-on-screen mark that **session grants are in force** — that MCP
 * terminal commands on the named servers are running without asking.
 *
 * This is not decoration; it is half of what makes the grant defensible.
 * `terminal_approval.rs`'s amended-O42 header promises grants are "revocable
 * and visible", and the original O42 objection to the old `Auto-run` switch was
 * precisely that one sticky click removed the gate for everything after it with
 * nothing on screen to say so. A grant the user has to remember giving is that
 * switch again.
 *
 * Since the owner's 14.09.2026 amendment there is one grant PER SERVER (at most
 * five), so this lists every one: each row names its server, shows both bounds
 * counting down (commands left and minutes left, because either can end it
 * first), and has its own Stop — one click, no confirm, ending only that
 * server. With more than one row, Stop all ends every grant at once. The way
 * out of a reduced-safety state must never be harder than the way in.
 *
 * Renders nothing when there is no grant, which is the overwhelmingly common
 * case and the reason this can sit at the app root.
 */
export default function McpSessionGrantPill({ grants, onRevoke, t }) {
  const list = Array.isArray(grants) ? grants : grants ? [grants] : [];
  if (list.length === 0) return null;
  return (
    <div className="mcp-grant-stack" data-testid="mcp-session-grant-pill">
      {list.map((grant) => {
        const minutes = Math.max(1, Math.ceil((Number(grant.expiresInSeconds) || 0) / 60));
        return (
          <div className="mcp-grant-pill" role="status" key={grant.connId} data-testid={`mcp-session-grant-${grant.connId}`}>
            <span className="mcp-grant-dot" aria-hidden="true" />
            <span className="mcp-grant-text">
              <span className="mcp-grant-title">
                {t('mcp.grantPillTitle', { name: grant.connName || grant.connId })}
              </span>
              <span className="mcp-grant-bounds">
                {t('mcp.grantPillBounds', { remaining: grant.remaining, minutes })}
              </span>
            </span>
            <button
              type="button"
              className="mcp-grant-stop"
              onClick={() => onRevoke(grant.connId)}
              data-testid="mcp-session-grant-revoke"
            >
              {t('mcp.grantPillStop')}
            </button>
          </div>
        );
      })}
      {list.length > 1 && (
        <button
          type="button"
          className="mcp-grant-stop mcp-grant-stop-all"
          onClick={() => onRevoke(null)}
          data-testid="mcp-session-grant-revoke-all"
        >
          {t('mcp.grantPillStopAll')}
        </button>
      )}
    </div>
  );
}
